import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { History, ChevronDown, ChevronUp, User, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Friendly labels for the raw audit `action` strings emitted by the backend.
const ACTION_LABELS = {
  'invoice.update':        { label: 'Edited invoice',       color: 'text-blue-700 bg-blue-50 border-blue-200' },
  'invoice.send':          { label: 'Sent to client',       color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  'invoice.mark_paid':     { label: 'Marked paid',          color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  'invoice.void':          { label: 'Voided',               color: 'text-amber-700 bg-amber-50 border-amber-200' },
  'invoice.cancel':        { label: 'Cancelled',            color: 'text-amber-700 bg-amber-50 border-amber-200' },
  'invoice.sync':          { label: 'Synced with Stripe',   color: 'text-purple-700 bg-purple-50 border-purple-200' },
  'invoice.archive':       { label: 'Archived',             color: 'text-zinc-700 bg-zinc-100 border-zinc-200' },
  'invoice.restore':       { label: 'Restored from archive', color: 'text-zinc-700 bg-zinc-100 border-zinc-200' },
  'invoice.delete':        { label: 'Deleted',              color: 'text-red-700 bg-red-50 border-red-200' },
  'invoice.force_delete':  { label: 'Force deleted',        color: 'text-red-700 bg-red-100 border-red-300' },
  'invoice.create':        { label: 'Created',              color: 'text-zinc-700 bg-zinc-50 border-zinc-200' },
  'invoice.schedule_send': { label: 'Scheduled send',       color: 'text-blue-700 bg-blue-50 border-blue-200' },
  'invoice.cancel_scheduled_send': { label: 'Cancelled scheduled send', color: 'text-amber-700 bg-amber-50 border-amber-200' },
};

const InvoiceAuditTrail = ({ invoiceId, headers, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [entries, setEntries] = useState(null); // null = not loaded, [] = loaded empty
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin-invoices/${invoiceId}/audit-log`, { headers });
      setEntries(res.data.entries || []);
    } catch (err) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, headers]);

  useEffect(() => {
    if (open && entries === null) load();
  }, [open, entries, load]);

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden" data-testid="invoice-audit-trail">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
        data-testid="audit-trail-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-zinc-500" />
          <span className="text-sm font-semibold text-zinc-800">Edit history</span>
          {entries !== null && entries.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold">
              {entries.length}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="p-3 bg-white max-h-72 overflow-y-auto">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div>
          ) : !entries || entries.length === 0 ? (
            <p className="text-xs text-zinc-500 py-3 text-center">No audit history yet.</p>
          ) : (
            <ol className="relative pl-4 space-y-3">
              <div className="absolute left-1 top-1 bottom-1 w-px bg-zinc-200" />
              {entries.map(e => {
                const cfg = ACTION_LABELS[e.action] || { label: e.action, color: 'text-zinc-700 bg-zinc-50 border-zinc-200' };
                const changed = e.metadata?.changed_fields || [];
                return (
                  <li key={e.id} className="relative" data-testid={`audit-entry-${e.id}`}>
                    <span className="absolute -left-[10px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#F56F05] border-2 border-white" />
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          {e.action === 'invoice.update' && changed.length > 0 && (
                            <span className="text-[11px] text-zinc-600">
                              {changed.slice(0, 4).join(', ')}
                              {changed.length > 4 && ` +${changed.length - 4} more`}
                            </span>
                          )}
                          {e.action === 'invoice.force_delete' && e.metadata?.had_stripe && (
                            <span className="text-[10px] text-red-600 italic">Stripe record kept</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-zinc-500">
                          <User className="w-3 h-3" />
                          <span className="font-medium text-zinc-700">{e.actor_name || e.actor_email || 'Unknown admin'}</span>
                        </div>
                      </div>
                      <span className="text-[10px] text-zinc-400 whitespace-nowrap flex-shrink-0">
                        {new Date(e.created_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceAuditTrail;
